import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLocation } from 'react-router';
import {
    HeadingText50,
    HeadingText30,
    BodyTextMedium,
    Button,
    SectionHeaderSkeleton,
    PageHeaderSkeleton,
    ButtonGroup,
} from '@modernhealth/vital-components';
import { FormattedDate, FormattedTime } from 'react-intl';
import { useFetchSessionQuery, useGetCalendarLinksQuery } from '@modernhealth/circles-graphql/v2';
import { useTranslation } from 'react-i18next';

import { InternalLinkButton } from 'src/shared/components/InternalLinkButton';

import { ReactComponent as Confirmation } from '../assets/confirmation.svg';
import { SessionCardRowSkeleton } from '../components/SessionCardSkeletons';
import {
    CirclesAddToCalendarModal,
    useCalendarLinksForCircle,
} from '../components/CirclesAddToCalendarButton';
import { BackButton } from '../components/BackButton';
import { Page } from '../components/PageLayout';
import { TranslatedDuration } from '../components/TranslatedDuration';

interface CirclesRsvpConfirmationPageParams {
    rsvpId: string;
    topicSlug: string;
}

interface CirclesRsvpConfirmationPageState {
    email?: string;
    sessionId: string;
}

const PageLayout: React.FC = ({ children }) => {
    return (
        <Page.Container>
            <Page.Header>
                <BackButton />
            </Page.Header>
            <Page.Content
                centered
                alignChildren="center"
                css={{
                    paddingTop: 70,
                    maxWidth: 700,
                }}
            >
                {children}
            </Page.Content>
        </Page.Container>
    );
};

const CirclesRsvpConfirmationPageSkeleton = (): JSX.Element => {
    return (
        <PageLayout>
            <PageHeaderSkeleton />

            <SectionHeaderSkeleton />
            <SessionCardRowSkeleton />

            <SectionHeaderSkeleton />
            <SessionCardRowSkeleton />

            <SectionHeaderSkeleton />
            <SessionCardRowSkeleton />
        </PageLayout>
    );
};

export const CirclesRsvpConfirmationPage: React.FC<unknown> = () => {
    const location = useLocation<CirclesRsvpConfirmationPageState | undefined>();
    const { email, sessionId = '' } = location?.state ?? {};

    const { rsvpId } = useParams<CirclesRsvpConfirmationPageParams>();
    const sessionQuery = useFetchSessionQuery({
        variables: {
            sessionId,
        },
        skip: !sessionId,
    });
    const calendarLinksQuery = useGetCalendarLinksQuery({
        variables: {
            rsvpId,
        },
    });

    const { t } = useTranslation('circles');

    const [showModal, setShowModal] = useState(false);

    const calendarLinks = useCalendarLinksForCircle(calendarLinksQuery.data);

    if (sessionQuery.loading || calendarLinksQuery.loading) {
        return <CirclesRsvpConfirmationPageSkeleton />;
    }

    const session = sessionQuery.data?.circleSession;
    const topic = session?.topic;

    return (
        <PageLayout>
            <CirclesAddToCalendarModal
                visible={showModal}
                onClose={() => setShowModal(false)}
                calendarLinks={calendarLinks ?? {}}
            />
            <Confirmation height="15rem" width="15rem" css={{ marginBottom: '3rem' }} />
            <HeadingText50 as="h2">{t('pages.rsvp_confirmation.title')}</HeadingText50>
            {topic?.name ? (
                <HeadingText30 css={{ marginBottom: '1rem' }}>{topic.name}</HeadingText30>
            ) : null}
            {session?.startTime ? (
                <BodyTextMedium fontColor="gray55" css={{ marginBottom: '1rem' }}>
                    <FormattedDate
                        weekday="short"
                        month="short"
                        day="numeric"
                        value={session.startTime}
                    />
                    {' · '}
                    <FormattedTime
                        hour="numeric"
                        minute="numeric"
                        timeZoneName="short"
                        value={session.startTime}
                    />
                    {topic?.duration ? (
                        <span>
                            <TranslatedDuration minutes={topic.duration} />
                        </span>
                    ) : null}
                </BodyTextMedium>
            ) : null}
            <BodyTextMedium fontColor="gray55" numberOfLines={3}>
                {t(
                    'pages.rsvp_confirmation.instructions',
                    email ? { context: 'email', email } : {},
                )}{' '}
            </BodyTextMedium>
            <BodyTextMedium css={{ marginBottom: '32px' }} fontColor="gray55">
                {t('pages.rsvp_confirmation.note')}
            </BodyTextMedium>
            <ButtonGroup>
                <Button
                    variant="secondary"
                    size="large"
                    onClick={() => {
                        setShowModal(true);
                    }}
                >
                    {t('pages.rsvp_confirmation.actions.add_to_calendar')}
                </Button>
                <InternalLinkButton variant="primary" size="large" to="/circles">
                    {t('pages.rsvp_confirmation.actions.explore_more_circles')}
                </InternalLinkButton>
            </ButtonGroup>
        </PageLayout>
    );
};
